import { toDayJs } from '~/services/TimeService';

import { ReopenImpact } from '~/types/reopen';
import { ExistingBlackoutDate, StopsellDateRange } from '~/types/stopsell';

export function calculateReopenImpact(
  reopenRange: StopsellDateRange,
  blackout: ExistingBlackoutDate,
): ReopenImpact | null {
  const rs = toDayJs(reopenRange.start);
  const re = toDayJs(reopenRange.end);
  const bs = toDayJs(blackout.start);
  const be = toDayJs(blackout.end);

  if (re.isBefore(bs) || rs.isAfter(be)) return null;
  if (blackout.blocks.length === 0) return null;

  if (!rs.isAfter(bs) && !re.isBefore(be)) {
    return { type: 'clear', blackout, remainders: [] };
  }

  if (!bs.isAfter(rs) && !be.isBefore(re)) {
    const beforeEnd = rs.subtract(1, 'day').format('YYYY-MM-DD');
    const afterStart = re.add(1, 'day').format('YYYY-MM-DD');
    const hasBefore = bs.isBefore(rs);
    const hasAfter = be.isAfter(re);
    const remainders: Array<StopsellDateRange> = [];
    if (hasBefore) remainders.push({ start: blackout.start, end: beforeEnd });
    if (hasAfter) remainders.push({ start: afterStart, end: blackout.end });
    const type = hasBefore && hasAfter ? 'split' : hasBefore ? 'trim_end' : 'trim_start';
    return { type, blackout, remainders };
  }

  if (rs.isBefore(bs) || rs.isSame(bs)) {
    const afterStart = re.add(1, 'day').format('YYYY-MM-DD');
    return {
      type: 'trim_start',
      blackout,
      remainders: [{ start: afterStart, end: blackout.end }],
    };
  }

  const beforeEnd = rs.subtract(1, 'day').format('YYYY-MM-DD');
  return {
    type: 'trim_end',
    blackout,
    remainders: [{ start: blackout.start, end: beforeEnd }],
  };
}

export function calculateAllReopenImpacts(
  reopenRange: StopsellDateRange,
  blackouts: Array<ExistingBlackoutDate>,
): Array<ReopenImpact> {
  return blackouts.map((b) => calculateReopenImpact(reopenRange, b)).filter((i): i is ReopenImpact => i !== null);
}
